// /dnd is a compact, DM-focused sibling of /series. Keep this module Node-prerender-safe: MUI,
// react-router and the pure splash helpers only. The signed-in store and Amplify graph cannot enter it.
import { Link as RouterLink } from 'react-router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CasinoIcon from '@mui/icons-material/CasinoOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHighOutlined';
import BubbleChartIcon from '@mui/icons-material/BubbleChartOutlined';
import HeadphonesIcon from '@mui/icons-material/HeadphonesOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import MapIcon from '@mui/icons-material/MapOutlined';
import PetsIcon from '@mui/icons-material/PetsOutlined';
import ShareIcon from '@mui/icons-material/ShareOutlined';
import { SplashChrome } from './splash-chrome';
import { Band, CtaBand, FeatureCard, SectionBody, SectionHeader } from './splash-sections';
import { metaDescription, ORIGIN, softwareApplicationLd, splashMeta } from './series-ld';
import { starterAuthUrl } from './series-demo';
import type { Route } from './+types/dnd';

const TITLE = 'AI D&D Campaign Chronicle and Narration | Gnothi';
const DESCRIPTION = metaDescription(
  'Turn a campaign premise and the notes you choose into an ongoing fantasy chronicle, then read it or listen with narration.',
);
const PATH = '/dnd';
const CAMPAIGN_STARTER_URL = starterAuthUrl('campaign-chronicle');

export const meta: Route.MetaFunction = () => [
  ...splashMeta({ title: TITLE, description: DESCRIPTION, path: PATH }),
  {
    'script:ld+json': softwareApplicationLd({
      name: 'Gnothi for D&D campaigns',
      description: DESCRIPTION,
      url: `${ORIGIN}${PATH}`,
      applicationCategory: 'MultimediaApplication',
      featureList: [
        'Campaign chronicle chapters',
        'Explicit note source choices',
        'Narrated chapters',
        'Optional podcast RSS publishing',
      ],
    }),
  },
];

function CampaignSample() {
  return (
    <Card
      variant="outlined"
      data-testid="dnd-campaign-sample"
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: 8,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          minHeight: 92,
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: (theme) => `linear-gradient(120deg, ${theme.palette.action.hover}, ${theme.palette.background.paper})`,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <MapIcon sx={{ fontSize: 45, color: 'feature.organization' }} />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <AutoFixHighIcon sx={{ transform: 'translateY(-4px)', color: 'feature.prompt' }} />
          <BubbleChartIcon sx={{ transform: 'translateY(6px)', color: 'feature.themes' }} />
          <PetsIcon sx={{ transform: 'translateY(-4px)', color: 'feature.organization' }} />
        </Stack>
        <CasinoIcon sx={{ fontSize: 42, color: 'feature.themes', transform: 'rotate(12deg)' }} />
      </Box>
      <Stack spacing={1.5} sx={{ p: { xs: 2.25, sm: 3 } }}>
        <Typography variant="overline" component="p" sx={{ color: 'splash.accent', fontWeight: 800 }}>
          Illustrative campaign excerpt · The Tollbooth of Teeth
        </Typography>
        <Typography component="p" variant="body2">
          By the time the gelatinous cube reached the tollbooth, it had collected three helmets, a chandelier, and the mayor's ceremonial trousers. Mira flattened herself behind a milestone while Brother Cal held up the party's last silver piece and asked whether the road accepted exact change.
        </Typography>
        <Typography component="p" variant="body2">
          The cube produced a receipt. This would have been reassuring if the receipt had not listed each adventurer under <em>items to be acquired</em>. Somewhere inside the wobbling blue wall, a tiny brass bell rang and the trousers waved like a surrender flag.
        </Typography>
        <Typography component="p" variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
          Then the dragon on customs duty leaned out of its booth, adjusted a pair of half-moon spectacles, and announced a surprise inspection. Mira looked at the chandelier. Cal looked at the dragon. The cube rolled one patient foot closer.
        </Typography>
      </Stack>
    </Card>
  );
}

function Hero() {
  return (
    <Band color="dark" hero>
      <Stack spacing={{ xs: 2, md: 3 }} sx={{ alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center', maxWidth: 820 }}>
          <Typography variant="overline" component="p" sx={{ color: 'splash.accentOnDark', fontWeight: 800 }}>
            For dungeon masters
          </Typography>
          <Typography variant="h2" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
            Turn your campaign into a story you can listen to
          </Typography>
          <Typography component="p" sx={{ mt: 1, opacity: 0.86, display: { xs: 'none', sm: 'block' } }}>
            Turn session notes into lively scenes between games, with narration included. You can also ask for separate ideas while preparing the next session.
          </Typography>
        </Box>
        <Box sx={{ width: '100%', maxWidth: 760 }}><CampaignSample /></Box>
        <Stack spacing={1} sx={{ alignItems: 'center', textAlign: 'center' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button component={RouterLink} to={CAMPAIGN_STARTER_URL} variant="contained" size="large" sx={{ px: 4 }}>
              Start a campaign Series
            </Button>
            <Button component="a" href="#sources" variant="outlined" color="inherit" size="large">See source choices</Button>
          </Stack>
          <Typography variant="caption" sx={{ color: 'grey.300' }}>You choose whether it starts with notes or with the premise alone.</Typography>
        </Stack>
      </Stack>
    </Band>
  );
}

function Steps() {
  return (
    <Band id="how-it-works">
      <SectionHeader
        icon={<MapIcon />}
        tone="organization"
        eyebrow="Between sessions"
        title="From the table to a chapter"
        deck="Describe the campaign, pick the notes that belong in it, then turn the night at the table into something worth reading or hearing again."
      />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="1. Describe the campaign" tone="organization">
            Set the world, party, tone, and the threads you want the next chapter to follow. Edit the campaign starter before anything is generated.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="2. Choose the notes" tone="themes">
            Use your own campaign-tagged notes, choose the broader journal option when shared player perspectives should join them, or begin from the premise alone.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="3. Read, listen, share" tone="prompt">
            Generate a chapter, read it, and listen to its narration. Publishing an RSS feed and sharing the result remain choices you control.
          </FeatureCard>
        </Grid>
      </Grid>
    </Band>
  );
}

function Sources() {
  return (
    <Band id="sources" color="grey">
      <SectionHeader
        icon={<LockIcon />}
        tone="themes"
        title="Choose what belongs in this campaign"
        deck="Keep it to your campaign notes, invite selected player perspectives, or start writing before anyone has taken notes."
      />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="Own campaign notes" tone="themes">
            Pick one or more of your eligible campaign tags. Past and future notes under those tags can contribute; shared player journals stay out of this mode.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="Own journal plus shared perspectives" tone="themes">
            Choose the broader journal option. It can include your eligible journal and player contributions they deliberately shared with you, so it is wider than one campaign tag.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="No notes yet" tone="themes">
            Start from the campaign premise and topics alone. You can connect eligible notes later when you shape the next chapter.
          </FeatureCard>
        </Grid>
      </Grid>
      <SectionBody center>
        A shared group does not quietly feed its posts into the chronicle. Players choose which perspectives to share. If you want a tightly bounded source, summarize those contributions in your own campaign-tagged notes and use Own campaign notes.
      </SectionBody>
    </Band>
  );
}

function ChronicleAndPrep() {
  return (
    <Band id="chronicle">
      <SectionHeader
        icon={<CasinoIcon />}
        tone="prompt"
        title="Retell the session. Keep future ideas separate."
        deck="The campaign starter follows your completed-session notes as far as they go. Ask separately when you want ideas for a future session."
      />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FeatureCard title="Chronicle what was recorded" tone="organization">
            With completed-session notes, the starter turns the recorded action into scenes and stops where those notes stop. Gnothi can use only the sources you provide.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FeatureCard title="Explore what might happen next" tone="prompt">
            Treat future scenes as ideas for the DM. They are speculative, separate from the campaign record, and never promises about what players will choose. Keep spoilers private until you decide to share them.
          </FeatureCard>
        </Grid>
      </Grid>
      <SectionBody center>
        Without session notes, the starter writes an imagined opening adventure from your campaign brief and labels it as fiction. You can edit the brief before chapter one, then steer later chapters from the Series itself.
      </SectionBody>
    </Band>
  );
}

function Listen() {
  return (
    <Band id="listen" color="dark">
      <SectionHeader
        icon={<HeadphonesIcon />}
        tone="themes"
        title="Let the party hear the chronicle"
        deck="Every generated chapter includes narration. RSS publishing, EPUB export, public pages, and directory placement are separate options."
      />
      <SectionBody center>
        Keep the Series private, or choose a public feed tier, confirm your publishing rights, and add the RSS feed to a podcast app. Gnothi does not automatically publish spoilers or submit episodes to third-party directories.{' '}
        <Link component={RouterLink} to="/series" sx={{ color: 'splash.accentOnDark' }}>See all Series and audio features</Link>{' · '}
        <Link component={RouterLink} to="/publishing" sx={{ color: 'splash.accentOnDark' }}>Open the publishing guide →</Link>
      </SectionBody>
    </Band>
  );
}

export default function DndPage() {
  return (
    <SplashChrome active="dnd">
      <Hero />
      <Steps />
      <Sources />
      <ChronicleAndPrep />
      <Listen />
      <CtaBand
        color="light"
        title="Bring the next chapter to the table"
        deck="Start with the campaign premise, choose its sources, and keep the surprising parts where you want them."
        label="Start a campaign Series"
        to={CAMPAIGN_STARTER_URL}
      />
    </SplashChrome>
  );
}
